import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpc, DATA_MUTATED_EVENT } from "@/lib/trpc";
import UserDisplay from "@/components/shared/UserDisplay";

function fmtTs(d: string | Date | null | undefined): string {
  if (!d) return "";
  return new Date(d).toLocaleString("vi-VN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * "Tạo lúc … bởi … | Cập nhật lúc … bởi …" cho chi tiết mở dạng ngăn kéo / hộp thoại (30/10/2026),
 * cùng nguồn getAuditInfo và cùng mã test như chân trang của StandardDetailLayout.
 */
export function EntityAuditFooter({
  entityType,
  entityId,
  fallback,
  className,
}: {
  entityType: string;
  entityId?: string | null;
  fallback?: { created?: string | Date | null; updated?: string | Date | null };
  className?: string;
}) {
  const enabled = Boolean(entityId && entityType);
  const { data: info, refetch } = useQuery({
    ...trpc.activityLog.getAuditInfo.queryOptions({ entityType, entityId: entityId || "" }),
    enabled,
    staleTime: 0,
  });

  useEffect(() => {
    if (!enabled) return;
    const onMutated = () => void refetch();
    window.addEventListener(DATA_MUTATED_EVENT, onMutated);
    return () => window.removeEventListener(DATA_MUTATED_EVENT, onMutated);
  }, [enabled, refetch]);

  const a = (info || {}) as any;
  const createdAt = a.createdAt || fallback?.created;
  const updatedAt = a.updatedAt || fallback?.updated;
  if (!createdAt && !updatedAt) return null;

  return (
    <footer
      data-testid="detail-audit-footer"
      aria-label="Thông tin tạo và cập nhật"
      className={className || "text-[11px] text-slate-500 pt-2 border-t border-slate-100 flex items-center gap-x-3 gap-y-1 flex-wrap"}
    >
      {createdAt && (
        <div className="flex items-center gap-1.5" data-testid="audit-created">
          <span>
            Tạo lúc <span data-testid="audit-created-at">{fmtTs(createdAt)}</span>
          </span>
          {a.createdByName && (
            <>
              <span className="text-slate-400">bởi</span>
              <span data-testid="audit-created-by">
                <UserDisplay name={a.createdByName} avatar={a.createdByAvatar} size="xs" textColor="text-slate-700" />
              </span>
            </>
          )}
        </div>
      )}
      {createdAt && updatedAt && <span className="text-slate-300" aria-hidden="true">|</span>}
      {updatedAt && (
        <div className="flex items-center gap-1.5" data-testid="audit-updated">
          <span>
            Cập nhật lúc <span data-testid="audit-updated-at">{fmtTs(updatedAt)}</span>
          </span>
          {a.updatedByName && (
            <>
              <span className="text-slate-400">bởi</span>
              <span data-testid="audit-updated-by">
                <UserDisplay name={a.updatedByName} avatar={a.updatedByAvatar} size="xs" textColor="text-slate-700" />
              </span>
            </>
          )}
        </div>
      )}
    </footer>
  );
}

export default EntityAuditFooter;
